import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Badge } from '@/components/ui/badge';
import type { AiUserUsage } from '../types/analytics.types';

interface AiUsageTableProps {
  data: AiUserUsage[] | undefined;
  isLoading: boolean;
}

const OP_LABELS: Record<string, string> = {
  cv_extraction: 'Extraction CV',
  embedding: 'Embedding',
  semantic_search: 'Recherche IA',
  matching: 'Matching',
  text_generation: 'Génération',
};

function formatCost(cents: number): string {
  return (cents / 100).toFixed(2) + ' $';
}

function formatTokens(tokens: number): string {
  if (tokens >= 1_000_000) return (tokens / 1_000_000).toFixed(1) + 'M';
  if (tokens >= 1_000) return (tokens / 1_000).toFixed(1) + 'k';
  return String(tokens);
}

function getTopOperation(operations: Record<string, number>): string {
  const entries = Object.entries(operations);
  if (entries.length === 0) return '-';
  entries.sort((a, b) => b[1] - a[1]);
  return OP_LABELS[entries[0][0]] ?? entries[0][0];
}

export function AiUsageTable({ data, isLoading }: AiUsageTableProps) {
  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base">Usage IA par utilisateur</CardTitle>
      </CardHeader>
      <CardContent>
        {isLoading ? (
          <div className="space-y-3">
            {Array.from({ length: 5 }).map((_, i) => (
              <div key={i} className="h-10 bg-muted animate-pulse rounded" />
            ))}
          </div>
        ) : !data || data.length === 0 ? (
          <div className="py-8 text-center text-muted-foreground">
            Aucune utilisation IA
          </div>
        ) : (
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead>Utilisateur</TableHead>
                <TableHead className="text-right">Requêtes</TableHead>
                <TableHead className="text-right">Tokens</TableHead>
                <TableHead className="text-right">Coût</TableHead>
                <TableHead>Opération principale</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {data.map((user) => (
                <TableRow key={user.userId}>
                  <TableCell>
                    <div className="flex items-center gap-3">
                      <div className="h-8 w-8 rounded-full bg-copper/20 flex items-center justify-center text-copper font-semibold text-xs">
                        {user.firstName[0]}{user.lastName[0]}
                      </div>
                      <span className="font-medium text-sm">
                        {user.firstName} {user.lastName}
                      </span>
                    </div>
                  </TableCell>
                  <TableCell className="text-right font-medium">{user.totalRequests}</TableCell>
                  <TableCell className="text-right text-muted-foreground">
                    {formatTokens(user.totalTokens)}
                  </TableCell>
                  <TableCell className="text-right font-medium">
                    {formatCost(user.totalCostCents)}
                  </TableCell>
                  <TableCell>
                    <Badge variant="secondary" className="text-xs">
                      {getTopOperation(user.operations)}
                    </Badge>
                  </TableCell>
                </TableRow>
              ))}
            </TableBody>
          </Table>
        )}
      </CardContent>
    </Card>
  );
}
